/**
 * Client-side CSV sampling helpers for the import wizard.
 *
 * The wizard's step-1 gate posts the user's CSV to `/api/v1/import/{entity}/validate`,
 * which is preview-only and caps at MAX_VALIDATE_ROWS (5,000). We slice to a header +
 * SAMPLE_ROW_COUNT data rows before that call, and post the full file to `/upload`.
 * See thoughts/shared/plans/2026-05-28-adv-973.md.
 */

export const SAMPLE_ROW_COUNT = 100;

export async function sampleCsvFile(
  file: File,
  maxDataRows: number,
): Promise<File> {
  const text = await file.text();
  const sliced = sliceCsvText(text, maxDataRows);
  if (sliced === text) return file;
  return new File([sliced], file.name, { type: "text/csv" });
}

export async function countCsvRows(file: File): Promise<number> {
  const text = await file.text();
  return countDataRows(text);
}

/**
 * Returns the substring of `text` containing the header row + the first
 * `maxDataRows` data rows. Returns `text` unchanged when the input has at most
 * `maxDataRows` data rows. Quoted newlines (RFC 4180) do not count as row
 * boundaries. Whitespace-only physical lines are skipped (matching the
 * backend parser's `skipEmptyLines: "greedy"` in
 * `packages/services/src/core/csv-parser.ts`) so a file padded with blank
 * lines doesn't get sampled into a no-rows file. Preserves the input's `\n`
 * vs `\r\n` line endings.
 */
function sliceCsvText(text: string, maxDataRows: number): string {
  if (text.length === 0) return text;

  let inQuote = false;
  let dataRowsSeen = 0;
  let seenHeader = false;
  let currentRowHasContent = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuote = !inQuote;
      currentRowHasContent = true;
      continue;
    }
    if (inQuote) {
      currentRowHasContent = true;
      continue;
    }
    if (ch === "\n") {
      // Whitespace-only line: skip (matches backend `skipEmptyLines: "greedy"`).
      if (!currentRowHasContent) continue;
      currentRowHasContent = false;

      if (!seenHeader) {
        seenHeader = true;
        if (maxDataRows === 0) return text.slice(0, i + 1);
        continue;
      }

      dataRowsSeen += 1;
      if (dataRowsSeen >= maxDataRows) {
        return text.slice(0, i + 1);
      }
      continue;
    }
    if (ch === "\r") continue;
    // Any other char on the line — only counts as content if non-whitespace.
    if (ch !== " " && ch !== "\t") currentRowHasContent = true;
  }

  return text;
}

/**
 * Counts data rows (non-header, non-empty) in `text`. Quoted newlines do not
 * split rows. Whitespace-only physical lines are skipped (matches the
 * backend parser's `skipEmptyLines: "greedy"`). A trailing empty record
 * after a terminating newline is not counted.
 */
function countDataRows(text: string): number {
  if (text.length === 0) return 0;

  let inQuote = false;
  let totalRows = 0;
  let currentRowHasContent = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuote = !inQuote;
      currentRowHasContent = true;
      continue;
    }
    if (inQuote) {
      currentRowHasContent = true;
      continue;
    }
    if (ch === "\n") {
      if (currentRowHasContent) totalRows += 1;
      currentRowHasContent = false;
      continue;
    }
    if (ch === "\r") continue;
    // Spaces and tabs alone don't make a line "content" — the backend's
    // `skipEmptyLines: "greedy"` setting skips whitespace-only lines.
    if (ch !== " " && ch !== "\t") currentRowHasContent = true;
  }

  if (currentRowHasContent) totalRows += 1;

  return Math.max(0, totalRows - 1);
}
